// Cross-tab softphone coordination, extracted from use-softphone so the
// ownership policy is unit-testable.
//
// Exactly one tab may own the SIP runtime: the FreeSWITCH internal profile is
// single-registration, so every extra REGISTER atomically replaces the
// previous tab's binding — two tabs running phones just flap the registration
// between them. Ownership is a localStorage lease held by the owner tab and
// is STICKY: a live owner keeps the phone even while hidden. Other tabs don't
// steal it on focus (that was the old model, and every steal cost a full
// runtime rebuild + REGISTER); instead they mirror the owner's phone state and
// route phone commands to it over a BroadcastChannel, so the phone is usable
// from every tab while only one connection/registration exists per agent.
//
// Why not a SharedWorker: WebRTC media and getUserMedia only run in a
// document, so a worker could never own the phone itself — a broadcast bus
// between documents gives the same single-connection behavior without a
// separate worker bundle.

import type { CallSummary, SoftphoneIncomingCall, SoftphoneState } from '@/types/telephony';

export const SOFTPHONE_OWNER_STORAGE_KEY = 'sales-suite:softphone-owner';
const SOFTPHONE_OWNER_TAB_ID_KEY = 'sales-suite:softphone-tab-id';
const SOFTPHONE_BUS_NAME = 'sales-suite:softphone-bus';
// TTL vs renew cadence: the owner renews every SOFTPHONE_OWNER_RENEW_MS on an
// interval — which Chrome throttles to once a minute in long-hidden tabs — and
// on every SIP SSE event/ping (network callbacks are never throttled, and the
// server pings every 15s). A lease can therefore only go stale when the owner
// page is gone/frozen or its stream AND timers are both dead — expiry means
// takeover of a dead phone, never tab-switch churn.
export const SOFTPHONE_OWNER_TTL_MS = 30_000;
export const SOFTPHONE_OWNER_RENEW_MS = 4_000;

export type SoftphoneOwnerLease = {
  tabId: string;
  updatedAt: number;
  // Published so a lame-duck loser can finish its make-before-break handoff
  // the moment the winner reports 'online'.
  phase: string | null;
};

function createTabId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `softphone-tab-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

export function getSoftphoneTabId(): string {
  if (typeof window === 'undefined') {
    return 'server';
  }
  try {
    const existing = window.sessionStorage.getItem(SOFTPHONE_OWNER_TAB_ID_KEY);
    if (existing) {
      return existing;
    }
    const nextId = createTabId();
    window.sessionStorage.setItem(SOFTPHONE_OWNER_TAB_ID_KEY, nextId);
    return nextId;
  } catch {
    return createTabId();
  }
}

export function readSoftphoneOwnerLease(): SoftphoneOwnerLease | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(SOFTPHONE_OWNER_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<SoftphoneOwnerLease>;
    if (typeof parsed.tabId !== 'string' || typeof parsed.updatedAt !== 'number' || !Number.isFinite(parsed.updatedAt)) {
      return null;
    }
    return {
      tabId: parsed.tabId,
      updatedAt: parsed.updatedAt,
      phase: typeof parsed.phase === 'string' ? parsed.phase : null,
    };
  } catch {
    return null;
  }
}

export function writeSoftphoneOwnerLease(lease: SoftphoneOwnerLease): void {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.setItem(SOFTPHONE_OWNER_STORAGE_KEY, JSON.stringify(lease));
}

export function clearSoftphoneOwnerLease(tabId: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  const current = readSoftphoneOwnerLease();
  if (current?.tabId === tabId) {
    window.localStorage.removeItem(SOFTPHONE_OWNER_STORAGE_KEY);
  }
}

// Sticky-ownership claim policy: claim only when there is no owner, the record
// is our own, or the owner's lease has expired. A fresh lease held by another
// tab is NEVER claimable — not even by a visible tab over a hidden one. Tabs
// that don't own the phone mirror it over the bus instead of taking it.
export function canClaimSoftphoneLease(current: SoftphoneOwnerLease | null, ownTabId: string, now: number = Date.now()): boolean {
  if (!current) {
    return true;
  }
  if (current.tabId === ownTabId) {
    return true;
  }
  return (now - current.updatedAt) > SOFTPHONE_OWNER_TTL_MS;
}

// ---- Cross-tab bus ---------------------------------------------------------

export type SoftphoneTabCommand =
  | { type: 'dial'; destination: string }
  | { type: 'answer' }
  | { type: 'reject' }
  | { type: 'hangup' }
  | { type: 'accept_incoming' }
  | { type: 'decline_incoming' }
  | { type: 'send_dtmf'; tone: string }
  | { type: 'set_muted'; muted: boolean }
  // Device prefs persist in (shared) localStorage; these commands exist so the
  // OWNER tab live-applies a change made from any other tab — the audio
  // element and peer connection only exist there.
  | { type: 'set_audio_input'; deviceId: string | null }
  | { type: 'set_audio_output'; deviceId: string | null }
  | { type: 'sync_identity'; campaignId: string }
  | { type: 'refresh_snapshot'; force: boolean }
  | { type: 'clear_action_error' }
  // Ask the owner tab for a fresh REGISTER (see useSoftphone.reRegister).
  | { type: 'reregister'; reason: string }
  | { type: 'reconnect' }
  | {
    type: 'telemetry';
    eventName: string;
    severity: 'info' | 'warning' | 'error';
    payload?: Record<string, unknown>;
    callId?: string | null;
  };

// Everything a non-owner tab needs to render the full softphone UI. All fields
// are plain JSON data (structured-clone-safe).
export type SoftphoneTabSnapshot = {
  ownerTabId: string;
  state: SoftphoneState;
  activeCall: CallSummary | null;
  recentCalls: CallSummary[];
  incomingCall: SoftphoneIncomingCall | null;
  localAudioMuted: boolean;
  reconnecting: boolean;
  // Stuck-media rebuilds ran out on the owner; optional for owners on the
  // previous build.
  mediaFailed?: boolean;
  // The owner's call is sending the switch no audio; optional for owners on
  // the previous build.
  silentUplink?: boolean;
  primaryDidNumber: string | null;
  actionError: string | null;
  // The owner's registration verdict, already past its grace window. Only the
  // owner holds a registerer, so without this a mirror renders green through a
  // registration outage it is reporting on.
  registration: { down: boolean; retrying: boolean };
};

export type SoftphoneBusMessage =
  // A tab without the lease announcing itself; the owner answers with a snapshot.
  | { kind: 'hello'; tabId: string }
  | ({ kind: 'snapshot' } & SoftphoneTabSnapshot)
  | { kind: 'command'; tabId: string; command: SoftphoneTabCommand };

export type SoftphoneTabBus = {
  post: (message: SoftphoneBusMessage) => void;
  subscribe: (listener: (message: SoftphoneBusMessage) => void) => () => void;
  close: () => void;
};

export function isSoftphoneBusMessage(data: unknown): data is SoftphoneBusMessage {
  if (!data || typeof data !== 'object') {
    return false;
  }
  const kind = (data as { kind?: unknown }).kind;
  return kind === 'hello' || kind === 'snapshot' || kind === 'command';
}

export function openSoftphoneTabBus(): SoftphoneTabBus | null {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
    return null;
  }
  const channel = new BroadcastChannel(SOFTPHONE_BUS_NAME);
  const listeners = new Set<(message: SoftphoneBusMessage) => void>();
  channel.onmessage = (event: MessageEvent) => {
    if (!isSoftphoneBusMessage(event.data)) {
      return;
    }
    for (const listener of Array.from(listeners)) {
      listener(event.data);
    }
  };
  return {
    post: (message) => {
      try {
        channel.postMessage(message);
      } catch {
        // Channel already closed (page teardown race) — nothing to deliver to.
      }
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    close: () => {
      listeners.clear();
      channel.close();
    },
  };
}
